import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import {
  Upload,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Loader2,
  ChevronDown,
  ChevronRight,
  RefreshCw,
  HardDrive,
  Users,
  Clock,
  Cpu,
  Camera,
  Tag,
  Settings,
  Shield,
  Wrench,
  ListChecks,
  Search,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const DEVICE_TYPE_OPTIONS = [
  {
    id: 'VI',
    title: 'VI',
    description: 'Video Intelligence'
  },
  {
    id: 'PSVI',
    title: 'PSVI',
    description: 'Platform Science Visual Intelligence'
  }
];

const normalizeTaskEntries = (rawEntries) => {
  if (!rawEntries) return [];
  return Array.isArray(rawEntries) ? rawEntries : [rawEntries];
};

const getEntrySerial = (entry) => (
  entry?.uniqueId ||
  entry?.deviceId ||
  entry?.serialNumber ||
  entry?.serial ||
  entry?.unique_id ||
  entry?.device_id ||
  null
);

const dedupeTaskResults = (entries) => {
  if (!Array.isArray(entries)) return [];

  const deduped = [];
  const seenKeys = new Set();

  // Deduplicate by serial only — taskId can be shared across devices in a batch,
  // so using it as the key would incorrectly drop all but one device.
  for (let i = entries.length - 1; i >= 0; i -= 1) {
    const entry = entries[i];
    const serial = entry?.serial || entry?.serial_number || entry?.deviceId || 'unknown';
    const key = String(serial);

    if (seenKeys.has(key)) {
      continue;
    }

    seenKeys.add(key);
    deduped.push(entry);
  }

  return deduped.reverse();
};

const PREFERENCE_KEYS = ['autoRefresh', 'upgradeViewMode', 'statusGroupFilter', 'showHistory', 'lastRefresh'];

const buildEntryMapBySerial = (entries) => {
  const map = new Map();
  entries.forEach(entry => {
    const serial = getEntrySerial(entry);
    if (serial) {
      const normalizedSerial = String(serial).trim();
      if (normalizedSerial) {
        map.set(normalizedSerial, entry);
      }
    }
  });
  return map;
};

const getDeviceKey = (device) => {
  if (!device) return null;
  const serial = String(device.serial || device.unique_id || device.uniqueId || '').trim();
  if (!serial) return null;
  return serial;
};

// Apps to disable for VI devices
const SYSMON_ALL_APPS = [
  'alarms',
  'data-logger',
  'diagnostics',
  'geolocation',
  'notifier',
  'safety',
  'vehicle-data-dispatcher',
  'vehicle-status',
  'video-events',
];

function logDebug(...args) {
  if (process.env.NODE_ENV !== 'production') {
    // eslint-disable-next-line no-console
    console.log(...args);
  }
}

function MigrationPanel({ devices, state, setState }) {
  const { user, isViewer } = useAuth();
  // Destructure persisted state
  const {
    selectedGroups: _selectedGroups,
    selectedRelease = null,
    selectedDeviceType = 'PSVI', // VI or PSVI
    selectedConfigTemplate = null, // Selected config template ID
    autoRefresh = true,
    upgradeViewMode = 'combined', // 'combined' or 'grouped'
    lastRefresh = null,
    statusGroupFilter = 'all'
  } = state || {};
  // Stabilize reference so a missing/undefined selectedGroups doesn't produce a new []
  // on every render (which would cause the group-fetch effect to fire on every render).
  const selectedGroups = useMemo(() => _selectedGroups ?? [], [_selectedGroups]);

  // taskResults is local state — hydrated from DB on mount via device group fetch
  const [taskResults, setTaskResultsRaw] = useState([]);

  // Local state (not persisted)
  const [availableGroups, setAvailableGroups] = useState([]);
  const [groupDevices, setGroupDevices] = useState([]);
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [releaseVersions, setReleaseVersions] = useState([]);
  const [releasesLoading, setReleasesLoading] = useState(false);
  const [taskLoading, setTaskLoading] = useState(false);
  const [taskError, setTaskError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  // const [stateLoaded, setStateLoaded] = useState(false); // Not used currently
  const [configTemplates, setConfigTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [configDropdownOpen, setConfigDropdownOpen] = useState(false);
  const [showCustomActions, setShowCustomActions] = useState(false);
  const autoRefreshIntervalRef = useRef(null);
  const needsInitialStatusPollRef = useRef(false);
  const [deviceGroupMap, setDeviceGroupMap] = useState({}); // Maps serial -> groupName
  const [showHistory, setShowHistory] = useState(false);
  const [deviceSelectionCollapsed, setDeviceSelectionCollapsed] = useState(false);
  const [selectedDeviceKeys, setSelectedDeviceKeys] = useState([]);
  const prevAvailableDeviceKeysRef = useRef([]);

  // Card expansion
  const [upgradeCardExpanded, setUpgradeCardExpanded] = useState(true);
  const [groupDropdownOpen, setGroupDropdownOpen] = useState(false);
  const [groupSearchTerm, setGroupSearchTerm] = useState('');
  const [releaseDropdownOpen, setReleaseDropdownOpen] = useState(false);
  const groupDropdownRef = useRef(null);
  const releaseDropdownRef = useRef(null);
  const configDropdownRef = useRef(null);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (groupDropdownRef.current && !groupDropdownRef.current.contains(event.target)) {
        setGroupDropdownOpen(false);
      }
      if (releaseDropdownRef.current && !releaseDropdownRef.current.contains(event.target)) {
        setReleaseDropdownOpen(false);
      }
      if (configDropdownRef.current && !configDropdownRef.current.contains(event.target)) {
        setConfigDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const getCurrentUserLabel = () => {
    return (
      user?.name ||
      user?.displayName ||
      user?.email ||
      user?.username ||
      'Unknown User'
    );
  };

  const formatDateTime = useCallback((value) => {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '-';
    return date.toLocaleString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  }, []);

  const preferenceStorageKey = useMemo(() => {
    if (!user) return null;
    const identifier = user.email || user.username || user.displayName || user.id || 'default';
    return `migrationPanelPrefs:${identifier}`;
  }, [user]);

  const persistPreferences = useCallback((newState) => {
    if (!preferenceStorageKey || typeof window === 'undefined') return;
    const payload = {};
    PREFERENCE_KEYS.forEach(key => {
      if (newState[key] !== undefined) {
        payload[key] = newState[key];
      }
    });
    try {
      window.localStorage.setItem(preferenceStorageKey, JSON.stringify(payload));
    } catch (err) {
      console.warn('Failed to persist migration preferences:', err);
    }
  }, [preferenceStorageKey]);

  // Helper to update persisted state and save preferences
  const updateState = useCallback((updates) => {
    if (!setState) return;
    setState(prev => {
      const newState = { ...prev, ...updates };
      persistPreferences(newState);
      return newState;
    });
  }, [setState, persistPreferences]);

  // Setters that update persisted state
  const setSelectedGroups = (value) => updateState({ selectedGroups: value });
  const setSelectedDeviceType = (value) => updateState({ selectedDeviceType: value });
  const setSelectedConfigTemplate = (value) => updateState({ selectedConfigTemplate: value });
  const setStatusGroupFilter = useCallback((value) => updateState({ statusGroupFilter: value }), [updateState]);

  // Toggle group selection
  const toggleGroupSelection = (groupName) => {
    if (selectedGroups.includes(groupName)) {
      setSelectedGroups(selectedGroups.filter(g => g !== groupName));
    } else {
      setSelectedGroups([...selectedGroups, groupName]);
    }
  };

  // Fleet lists run long enough that scrolling to find a name is the wrong interaction.
  const filteredGroups = useMemo(() => {
    const term = groupSearchTerm.trim().toLowerCase();
    if (!term) return availableGroups;
    return availableGroups.filter(g => g.name.toLowerCase().includes(term));
  }, [availableGroups, groupSearchTerm]);

  // Select All follows the search: with a term typed, "all" means the groups on screen,
  // which is what makes searching an action rather than just a way to look. It adds to
  // the current selection instead of replacing it, so narrowing twice accumulates.
  const selectAllGroups = () => setSelectedGroups(
    Array.from(new Set([...selectedGroups, ...filteredGroups.map(g => g.name)]))
  );
  const deselectAllGroups = () => setSelectedGroups([]);
  const setSelectedRelease = (value) => updateState({ selectedRelease: value });
  const setTaskResults = useCallback((value) => {
    if (typeof value === 'function') {
      setTaskResultsRaw(prev => {
        const next = dedupeTaskResults(value(prev));
        setState?.(s => ({ ...s, taskResults: next }));
        return next;
      });
    } else {
      const next = dedupeTaskResults(value);
      setTaskResultsRaw(next);
      setState?.(s => ({ ...s, taskResults: next }));
    }
  }, [setState]);
  const setAutoRefresh = useCallback((value) => updateState({ autoRefresh: value }), [updateState]);
  const setUpgradeViewMode = useCallback((value) => updateState({ upgradeViewMode: value }), [updateState]);
  const setLastRefresh = useCallback((value) => updateState({ lastRefresh: value }), [updateState]);

  // Track if initial load has been done
  const initialLoadDoneRef = useRef(false);

  // Load groups/releases on mount (single combined effect)
  useEffect(() => {
    // Prevent duplicate calls on re-renders
    if (initialLoadDoneRef.current) return;
    initialLoadDoneRef.current = true;

    const loadAllData = async () => {
      setLoadingGroups(true);
      setReleasesLoading(true);
      setTemplatesLoading(true);
      try {
        const [groupsResponse, releasesResponse, templatesResponse] = await Promise.all([
          axios.get('/api/device-groups'),
          axios.get('/api/release-versions'),
          axios.get('/api/config-templates')
        ]);

        setAvailableGroups(groupsResponse.data.groups || []);
        setReleaseVersions(releasesResponse.data.versions || []);
        setConfigTemplates(templatesResponse.data.templates || []);
      } catch (err) {
        console.error('Failed to load data:', err);
      } finally {
        setLoadingGroups(false);
        setReleasesLoading(false);
        setTemplatesLoading(false);
      }
    };
    loadAllData();
  }, []);

  // Load group devices when selected groups change
  useEffect(() => {
    const loadGroupDevices = async () => {
      if (!selectedGroups || selectedGroups.length === 0) {
        setGroupDevices([]);
        setDeviceGroupMap({});
        return;
      }

      try {
        // Fetch devices from all selected groups
        const responses = await Promise.all(
          selectedGroups.map(group =>
            axios.get(`/api/device-groups/${encodeURIComponent(group)}`)
          )
        );

        // Combine and deduplicate devices
        const seenDevices = new Map();
        const newDeviceGroupMap = {};

        responses.forEach((response, idx) => {
          const groupName = selectedGroups[idx];
          const devices = response.data.devices || [];
          devices.forEach(device => {
            const key = getDeviceKey(device);
            if (!key) {
              return;
            }
            if (!newDeviceGroupMap[key]) {
              newDeviceGroupMap[key] = new Set();
            }
            newDeviceGroupMap[key].add(groupName);
            if (!seenDevices.has(key)) {
              seenDevices.set(key, device);
            }
          });
        });

        const aggregatedDevices = [];
        const serialGroupMap = {};
        seenDevices.forEach((device, key) => {
          const groups = Array.from(newDeviceGroupMap[key] || []);
          const [serial] = key.split(':');
          // Take the first group (since device can only be in one group)
          serialGroupMap[serial] = groups[0] || null;
          aggregatedDevices.push({ ...device, group: groups[0] || null });
        });

        setGroupDevices(aggregatedDevices);
        setDeviceGroupMap(serialGroupMap);

        // Hydrate taskResults from DB-backed task IDs only; live status is fetched separately
        const allDevices = responses.flatMap(r => r.data.devices || []);
        const deviceTaskResults = allDevices
          .filter(d => d.upgrade_task_id || d.config_task_id)
          .map(d => ({
            serial: d.serial,
            taskId: d.upgrade_task_id,
            configTaskId: d.config_task_id,
            group: serialGroupMap[d.serial] || null,
            deviceType: d.serial.startsWith('007') ? 'M1N' : d.serial.startsWith('00D') ? 'Dashcam' : null,
          }));
        setTaskResults(deviceTaskResults);
        if (deviceTaskResults.some(t => t.taskId || t.configTaskId)) {
          needsInitialStatusPollRef.current = true;
        }
      } catch (err) {
        console.error('Failed to load group devices:', err);
        setGroupDevices([]);
        setDeviceGroupMap({});
      }
    };
    loadGroupDevices();
  }, [selectedGroups, setTaskResults]);

  // Keep device selection in sync with available devices, preserving manual choices
  useEffect(() => {
    const availableKeys = groupDevices.map(getDeviceKey).filter(Boolean);
    setSelectedDeviceKeys(prevSelected => {
      const prevSelectedSet = new Set(prevSelected);
      const prevAvailableSet = new Set(prevAvailableDeviceKeysRef.current);
      const wasSelectingAllPreviously = prevAvailableSet.size > 0 &&
        prevAvailableSet.size === prevSelectedSet.size &&
        Array.from(prevAvailableSet).every(key => prevSelectedSet.has(key));

      if (prevSelectedSet.size === 0 || wasSelectingAllPreviously) {
        return availableKeys;
      }

      return availableKeys.filter(key => prevSelectedSet.has(key));
    });
    prevAvailableDeviceKeysRef.current = availableKeys;
  }, [groupDevices]);

  const toggleDeviceSelection = useCallback((deviceKey) => {
    setSelectedDeviceKeys(prev => {
      if (prev.includes(deviceKey)) {
        return prev.filter(key => key !== deviceKey);
      }
      return [...prev, deviceKey];
    });
  }, []);

  const selectAllDevices = useCallback(() => {
    setSelectedDeviceKeys(groupDevices.map(getDeviceKey).filter(Boolean));
  }, [groupDevices]);

  const clearAllDevices = useCallback(() => {
    setSelectedDeviceKeys([]);
  }, []);

  const selectedDeviceKeySet = useMemo(() => new Set(selectedDeviceKeys), [selectedDeviceKeys]);
  const selectedGroupDevices = useMemo(
    () => groupDevices.filter(device => selectedDeviceKeySet.has(getDeviceKey(device))),
    [groupDevices, selectedDeviceKeySet]
  );

  // Categorize devices by type
  const m1nDevices = selectedGroupDevices.filter(d => d.serial.startsWith('007'));
  const dashcamDevices = selectedGroupDevices.filter(d => d.serial.startsWith('00D'));

  // Get selected files from release
  const selectedM1nFile = selectedRelease?.m1n_upgrade_file || null;
  const selectedDashcamFile = selectedRelease?.dashcam_upgrade_file || null;

  // Create upgrade tasks for all devices
  const handleCreateUpgradeTasks = async () => {
    if (!selectedRelease) {
      setTaskError('Please select a release version');
      return;
    }

    if (m1nDevices.length === 0 && dashcamDevices.length === 0) {
      setTaskError('No devices selected for upgrade');
      return;
    }

    setTaskLoading(true);
    setTaskError(null);

    const results = [];
    const creationTimestamp = new Date().toISOString();
    const startedBy = getCurrentUserLabel();
    const baseTaskFields = {
      createdAt: creationTimestamp,
      startedBy
    };

    // Get selected config template if any
    const selectedTemplate = selectedConfigTemplate
      ? configTemplates.find(t => t.id === selectedConfigTemplate)
      : null;

    try {
      // Group devices by type and create ONE task per type (batch upgrade)
      const deviceGroups = [];

      if (m1nDevices.length > 0 && selectedM1nFile) {
        deviceGroups.push({
          devices: m1nDevices,
          deviceType: 'M1N',
          file: selectedM1nFile
        });
      }

      if (dashcamDevices.length > 0 && selectedDashcamFile) {
        deviceGroups.push({
          devices: dashcamDevices,
          deviceType: 'Dashcam',
          file: selectedDashcamFile
        });
      }

      // Create one upgrade task per device type (batch)
      for (const group of deviceGroups) {
        const serials = group.devices.map(d => d.serial);
        logDebug(`[MigrationPanel] Creating batch upgrade task for ${group.deviceType}: ${serials.length} devices`);
        logDebug(`[MigrationPanel] Serials: ${serials.join(', ')}`);

        try {
          // Step 1: Create single upgrade task for all devices of this type
          const upgradeResponse = await axios.post('/api/ftcloud/maintenance-task', {
            unique_ids: serials,
            file_id: group.file.fileId || group.file.id,
            task_type: 'REMOTE_UPGRADE',
            video_solution: selectedDeviceType
          });

          const taskEntries = normalizeTaskEntries(upgradeResponse.data.data?.data);
          const taskEntryMap = buildEntryMapBySerial(taskEntries);
          const sharedTaskId = taskEntries[0]?.maintTaskId || taskEntries[0]?.taskId || taskEntries[0]?.id || null;

          logDebug(`[MigrationPanel] Batch upgrade task created. Received ${taskEntries.length} task entries for ${serials.length} ${group.deviceType} devices`);

          // Create result entry for each device in the batch
          for (const device of group.devices) {
            const entry = taskEntryMap.get(device.serial);
            const entryTaskId = entry?.maintTaskId || entry?.taskId || entry?.id || sharedTaskId;
            const entryStatus = entry?.status || entry?.taskStatus || entry?.maintStatus;
            const entryProgress = entry?.progress ?? entry?.upgradeProgress ?? entry?.percent;
            const entryMessage = entry?.message || entry?.statusDesc || entry?.maintStatusDesc;

            const deviceResult = {
              serial: device.serial,
              taskId: entryTaskId,
              status: upgradeResponse.data.success
                ? (entryStatus || 'PENDING')
                : 'FAILED',
              progress: upgradeResponse.data.success
                ? (entryProgress ?? 0)
                : 0,
              message: upgradeResponse.data.success
                ? (entryMessage || 'Batch task created')
                : (upgradeResponse.data.error || 'Failed to create task'),
              deviceType: group.deviceType,
              fileName: group.file.fileName || group.file.name,
              releaseVersion: selectedRelease.name,
              lastUpdate: new Date().toISOString(),
              group: deviceGroupMap[device.serial] || null,
              upgradeType: selectedDeviceType,
              suppressAppsStatus: (selectedDeviceType === 'VI' || selectedDeviceType === 'PSVI') ? 'PENDING' : null,
              suppressAppsTaskId: null,
              configTaskId: null,
              configStatus: null,
              configMessage: null,
              configTemplateName: null,
              ...baseTaskFields
            };
            results.push(deviceResult);
          }

          // Step 2: Create config task for the batch if template is selected and upgrade succeeded
          if (selectedTemplate && upgradeResponse.data.success) {
            logDebug(`[MigrationPanel] Creating batch config task for ${group.deviceType}...`);
            try {
              const configResponse = await axios.post('/api/ftcloud/maintenance-task', {
                unique_ids: serials,
                task_type: 'CUSTOM_PARAM_ISSUED',
                custom_param: JSON.stringify(selectedTemplate.config)
              });

              if (configResponse.data.success) {
                const configEntries = normalizeTaskEntries(configResponse.data.data?.data);
                const configEntryMap = buildEntryMapBySerial(configEntries);
                const sharedConfigTaskId = configEntries[0]?.maintTaskId || configEntries[0]?.taskId || configEntries[0]?.id || null;

                logDebug(`[MigrationPanel] ✅ Batch config task created. Received ${configEntries.length} config task entries`);

                // Update all devices in this batch with config task info
                results.forEach(r => {
                  if (serials.includes(r.serial)) {
                    const configEntry = configEntryMap.get(r.serial);
                    r.configTaskId = configEntry?.maintTaskId || configEntry?.taskId || configEntry?.id || sharedConfigTaskId;
                    r.configStatus = configEntry?.status || configEntry?.taskStatus || configEntry?.maintStatus || 'PENDING';
                    r.configMessage = configEntry?.message || configEntry?.statusDesc || configEntry?.maintStatusDesc || 'Batch config task created';
                    r.configTemplateName = selectedTemplate.name;
                  }
                });
              } else {
                console.error(`[MigrationPanel] ❌ Batch config task failed:`, configResponse.data.error);
                results.forEach(r => {
                  if (serials.includes(r.serial)) {
                    r.configTaskId = null;
                    r.configStatus = 'FAILED';
                    r.configMessage = configResponse.data.error || 'Failed to create config task';
                  }
                });
              }
            } catch (configErr) {
              console.error(`[MigrationPanel] ❌ Batch config task exception:`, configErr);
              results.forEach(r => {
                if (serials.includes(r.serial)) {
                  r.configTaskId = null;
                  r.configStatus = 'FAILED';
                  r.configMessage = configErr.response?.data?.error || configErr.message;
                }
              });
            }
          }

        } catch (upgradeErr) {
          // Batch upgrade task failed - mark all devices as failed
          console.error(`[MigrationPanel] ❌ Batch upgrade task failed for ${group.deviceType}:`, upgradeErr);

          for (const device of group.devices) {
            results.push({
              serial: device.serial,
              taskId: null,
              status: 'FAILED',
              progress: 0,
              message: upgradeErr.response?.data?.error || upgradeErr.message,
              deviceType: group.deviceType,
              fileName: group.file.fileName || group.file.name,
              releaseVersion: selectedRelease.name,
              lastUpdate: new Date().toISOString(),
              group: deviceGroupMap[device.serial] || null,
              upgradeType: selectedDeviceType,
              suppressAppsStatus: (selectedDeviceType === 'VI' || selectedDeviceType === 'PSVI') ? 'FAILED' : null,
              suppressAppsTaskId: null,
              configTaskId: null,
              configStatus: null,
              configMessage: null,
              configTemplateName: null,
              ...baseTaskFields
            });
          }
        }
      }

      setTaskResults(prevResults => {
        const existingResults = Array.isArray(prevResults) ? prevResults : [];
        return [...existingResults, ...results];
      });
      setLastRefresh(new Date());

    } catch (err) {
      setTaskError(err.message || 'Failed to create upgrade tasks');
    } finally {
      setTaskLoading(false);
    }
  };

  // Send custom configuration to selected devices
  const handleSendCustomConfig = async () => {
    if (!selectedConfigTemplate) {
      setTaskError('Please select a configuration template');
      return;
    }

    if (taskResults.length === 0) {
      setTaskError('No devices available. Please create upgrade tasks first.');
      return;
    }

    setTaskLoading(true);
    setTaskError(null);

    try {
      const selectedTemplate = configTemplates.find(t => t.id === selectedConfigTemplate);
      if (!selectedTemplate) {
        setTaskError('Selected config template not found');
        return;
      }

      const devicesToConfig = taskResults.filter(t =>
        (t.deviceType === 'M1N' || t.deviceType === 'Dashcam') &&
        t.status && ['SUCCESS', 'COMPLETED'].includes(t.status.toUpperCase())
      );

      if (devicesToConfig.length === 0) {
        setTaskError('No completed upgrade tasks found. Devices must complete upgrade before sending config.');
        return;
      }

      logDebug(`[MigrationPanel] Sending custom config to ${devicesToConfig.length} devices...`);

      for (const task of devicesToConfig) {
        try {
          const configResponse = await axios.post('/api/ftcloud/maintenance-task', {
            unique_ids: [task.serial],
            task_type: 'CUSTOM_PARAM_ISSUED',
            custom_param: JSON.stringify(selectedTemplate.config)
          });

          if (configResponse.data.success) {
            const configTaskData = configResponse.data.data?.data || [];
            const configTaskInfo = configTaskData[0] || {};

            setTaskResults(prev => prev.map(t =>
              t.serial === task.serial ? {
                ...t,
                configTaskId: configTaskInfo.maintTaskId || configTaskInfo.taskId || null,
                configStatus: 'PENDING',
                configMessage: 'Config task created',
                configTemplateName: selectedTemplate.name,
                lastUpdate: new Date().toISOString()
              } : t
            ));
            logDebug(`[MigrationPanel] ✅ Config sent to ${task.serial}`);
          } else {
            setTaskResults(prev => prev.map(t =>
              t.serial === task.serial ? {
                ...t,
                configTaskId: null,
                configStatus: 'FAILED',
                configMessage: configResponse.data.error || 'Failed to create config task',
                lastUpdate: new Date().toISOString()
              } : t
            ));
            console.error(`[MigrationPanel] ❌ Config failed for ${task.serial}`);
          }
        } catch (err) {
          setTaskResults(prev => prev.map(t =>
            t.serial === task.serial ? {
              ...t,
              configTaskId: null,
              configStatus: 'FAILED',
              configMessage: err.response?.data?.error || err.message,
              lastUpdate: new Date().toISOString()
            } : t
          ));
          console.error(`[MigrationPanel] ❌ Config exception for ${task.serial}:`, err);
        }
      }

      setLastRefresh(new Date());
    } catch (err) {
      setTaskError(err.message || 'Failed to send custom configuration');
    } finally {
      setTaskLoading(false);
    }
  };

  // Send suppress apps command (VI or PSVI)
  const handleSendSuppressApps = async () => {
    if (selectedDeviceType !== 'VI' && selectedDeviceType !== 'PSVI') {
      setTaskError('Suppress apps is only available for VI and PSVI device types');
      return;
    }

    if (taskResults.length === 0) {
      setTaskError('No devices available. Please create upgrade tasks first.');
      return;
    }

    setTaskLoading(true);
    setTaskError(null);

    try {
      const devicesToSuppress = taskResults.filter(t =>
        (t.upgradeType === 'VI' || t.upgradeType === 'PSVI') &&
        t.status && ['SUCCESS', 'COMPLETED'].includes(t.status.toUpperCase())
      );

      if (devicesToSuppress.length === 0) {
        setTaskError('No completed VI/PSVI upgrade tasks found. Devices must complete upgrade before suppressing apps.');
        return;
      }

      logDebug(`[MigrationPanel] Sending suppress apps to ${devicesToSuppress.length} devices...`);

      for (const task of devicesToSuppress) {
        try {
          // Check if device is registered in IoT Hub
          const iotCheckResponse = await axios.post('/api/azure/device/search', {
            serial: task.serial,
          });
          const deviceStatus = iotCheckResponse.data?.status;

          if (deviceStatus !== 'found') {
            setTaskResults(prev => prev.map(t =>
              t.serial === task.serial ? {
                ...t,
                suppressAppsStatus: 'WAITING_IOT',
                suppressAppsMessage: `Device not registered in IoT Hub (status: ${deviceStatus})`,
                lastUpdate: new Date().toISOString()
              } : t
            ));
            logDebug(`[MigrationPanel] ⏳ Device ${task.serial} not in IoT Hub yet`);
            continue;
          }

          // Send sysmon config via Device Twin
          // Use selectedDeviceType (current selection) instead of task.upgradeType (saved when task was created)
          const disabledApps = selectedDeviceType === 'VI' ? SYSMON_ALL_APPS : [''];
          const sysmonConfig = {
            sysmon: {
              disabledApps: disabledApps
            }
          };

          logDebug(`[MigrationPanel] Sending sysmon config to ${task.serial} (${selectedDeviceType}), disabledApps=${JSON.stringify(disabledApps)}`);

          const response = await axios.put('/api/device/config-binding', {
            serial_number: task.serial,
            config: sysmonConfig
          });

          console.log(`[MigrationPanel] Response received:`, response.data);

          if (response.data.success) {
            const message = selectedDeviceType === 'VI'
              ? 'Apps suppressed (all disabled)'
              : 'Sysmon config sent (no apps disabled)';

            setTaskResults(prev => prev.map(t =>
              t.serial === task.serial ? {
                ...t,
                suppressAppsStatus: 'SENT',
                suppressAppsMessage: message,
                lastUpdate: new Date().toISOString()
              } : t
            ));
            console.log(`[MigrationPanel] ✅ Sysmon sent to ${task.serial} (${selectedDeviceType})`);
          } else {
            setTaskResults(prev => prev.map(t =>
              t.serial === task.serial ? {
                ...t,
                suppressAppsStatus: 'FAILED',
                suppressAppsMessage: response.data.error || 'Failed to send sysmon config',
                lastUpdate: new Date().toISOString()
              } : t
            ));
            console.error(`[MigrationPanel] ❌ Sysmon failed for ${task.serial}`);
          }
        } catch (err) {
          console.error(`[MigrationPanel] ❌ Sysmon exception for ${task.serial}:`, err);
          console.error(`[MigrationPanel] Error details:`, {
            message: err.message,
            response: err.response?.data,
            status: err.response?.status,
            statusText: err.response?.statusText
          });
          setTaskResults(prev => prev.map(t =>
            t.serial === task.serial ? {
              ...t,
              suppressAppsStatus: 'FAILED',
              suppressAppsMessage: err.response?.data?.error || err.message,
              lastUpdate: new Date().toISOString()
            } : t
          ));
        }
      }

      setLastRefresh(new Date());
    } catch (err) {
      setTaskError(err.message || 'Failed to send suppress apps command');
    } finally {
      setTaskLoading(false);
    }
  };

  const extractStatusEntries = useCallback((payload) => {
    if (!payload) return [];

    const queue = [payload];
    const visited = new Set();
    const entryMap = new Map();
    const candidateKeys = ['data', 'records', 'items', 'list', 'result', 'content'];

    const pushEntry = (entry) => {
      if (!entry || typeof entry !== 'object') return;
      const key = entry.maintTaskId ?? entry.taskId ?? entry.id;
      if (key == null) return;
      const normalizedKey = String(key);
      entryMap.set(normalizedKey, { ...entryMap.get(normalizedKey), ...entry });
    };

    while (queue.length > 0) {
      const current = queue.shift();
      if (current == null || visited.has(current)) {
        continue;
      }
      visited.add(current);

      if (Array.isArray(current)) {
        current.forEach(item => {
          if (item && typeof item === 'object') {
            pushEntry(item);
            queue.push(item);
          }
        });
        continue;
      }

      if (typeof current === 'object') {
        pushEntry(current);
        candidateKeys.forEach(key => {
          if (current[key] !== undefined) {
            queue.push(current[key]);
          }
        });
      }
    }

    return Array.from(entryMap.values());
  }, []);

  // Refresh task status
  const refreshTaskStatus = useCallback(async () => {
    const tasksWithIds = taskResults.filter(t => t.taskId);
    const configTasksWithIds = taskResults.filter(t => t.configTaskId);
    if (tasksWithIds.length === 0 && configTasksWithIds.length === 0) return;

    setRefreshing(true);

    try {
      // Fetch firmware upgrade task status
      if (tasksWithIds.length > 0) {
        const taskIds = Array.from(new Set(tasksWithIds.map(t => t.taskId).filter(Boolean)));
        if (taskIds.length > 0) {
          const response = await axios.post('/api/ftcloud/maintenance-task/status', {
            task_ids: taskIds
          });

          console.log('[MigrationPanel] Task status response:', response.data);

          if (response.data.success) {
            const statusData = extractStatusEntries(response.data);
            const missingUpgradeIds = new Set(response.data.missing_task_ids || []);

            setTaskResults(prev => prev.map(task => {
              if (!task.taskId) return task;

              if (missingUpgradeIds.has(String(task.taskId))) {
                return { ...task, taskId: null };
              }

              const statusInfo = statusData.find(s =>
                String(s.maintTaskId) === String(task.taskId) ||
                String(s.taskId) === String(task.taskId) ||
                String(s.id) === String(task.taskId)
              );

              if (statusInfo) {
                const newStatus = statusInfo.status || statusInfo.taskStatus || statusInfo.maintStatus || task.status;
                const newProgress = statusInfo.progress ?? statusInfo.upgradeProgress ?? statusInfo.percent ?? task.progress;
                const newMessage = statusInfo.message || statusInfo.errorMsg || statusInfo.description ||
                  statusInfo.statusDesc || statusInfo.maintStatusDesc || task.message;

                return {
                  ...task,
                  status: newStatus,
                  progress: newProgress,
                  message: newMessage,
                  releaseVersion: statusInfo.releaseVersion || task.releaseVersion,
                  fileName: statusInfo.fileName || task.fileName,
                  lastUpdate: statusInfo.updateTime || statusInfo.updatedAt || statusInfo.updated_at || new Date().toISOString()
                };
              }
              return task;
            }).filter(t => t.taskId || t.configTaskId || t.status));

            setTaskError(null);
          } else {
            setTaskError(response.data.error || 'Failed to refresh upgrade status');
          }
        }
      }

      // Fetch config task status
      if (configTasksWithIds.length > 0) {
        const configTaskIds = Array.from(new Set(configTasksWithIds.map(t => t.configTaskId).filter(Boolean)));
        if (configTaskIds.length > 0) {
          const configResponse = await axios.post('/api/ftcloud/maintenance-task/status', {
            task_ids: configTaskIds
          });

          if (configResponse.data.success) {
            const configStatusData = extractStatusEntries(configResponse.data);
            const missingConfigIds = new Set(configResponse.data.missing_task_ids || []);

            setTaskResults(prev => prev.map(task => {
              if (!task.configTaskId) return task;

              if (missingConfigIds.has(String(task.configTaskId))) {
                return { ...task, configTaskId: null };
              }

              const configStatusInfo = configStatusData.find(s =>
                String(s.maintTaskId) === String(task.configTaskId) ||
                String(s.taskId) === String(task.configTaskId) ||
                String(s.id) === String(task.configTaskId)
              );

              if (configStatusInfo) {
                const newConfigStatus = configStatusInfo.status || configStatusInfo.taskStatus || configStatusInfo.maintStatus || task.configStatus;
                const newConfigMessage = configStatusInfo.message || configStatusInfo.errorMsg || configStatusInfo.description ||
                  configStatusInfo.statusDesc || configStatusInfo.maintStatusDesc || task.configMessage;

                return {
                  ...task,
                  configStatus: newConfigStatus,
                  configMessage: newConfigMessage,
                  lastUpdate: configStatusInfo.updateTime || configStatusInfo.updatedAt || configStatusInfo.updated_at || new Date().toISOString()
                };
              }
              return task;
            }).filter(t => t.taskId || t.configTaskId || t.status));
          } else {
            setTaskError(configResponse.data.error || 'Failed to refresh config task status');
          }
        }
      }

      setLastRefresh(new Date());
    } catch (err) {
      console.error('Failed to refresh task status:', err);
    } finally {
      setRefreshing(false);
    }
  }, [taskResults, setLastRefresh, setTaskResults, extractStatusEntries]);

  const retryTask = useCallback(async (task, field) => {
    const taskId = field === 'config' ? task.configTaskId : task.taskId;
    if (!taskId) return;
    setTaskResults(prev => prev.map(t =>
      t.serial === task.serial
        ? field === 'config'
          ? { ...t, configStatus: 'PENDING', configMessage: null }
          : { ...t, status: 'PENDING', message: null }
        : t
    ));
    try {
      await axios.post('/api/ftcloud/maintenance-task/retry', { task_ids: [String(taskId)] });
      await refreshTaskStatus();
    } catch (err) {
      console.error(`[MigrationPanel] Retry failed for ${task.serial}:`, err);
      setTaskResults(prev => prev.map(t =>
        t.serial === task.serial
          ? field === 'config'
            ? { ...t, configStatus: 'FAILED', configMessage: 'Retry request failed' }
            : { ...t, status: 'FAILED', message: 'Retry request failed' }
          : t
      ));
    }
  }, [refreshTaskStatus, setTaskResults]);

  // Process VI and PSVI devices: check if ready and send sysmon config
  const processSuppressApps = useCallback(async () => {
    console.log('[MigrationPanel] processSuppressApps called - checking tasks...');
    console.log('[MigrationPanel] Total tasks:', taskResults.length);

    // Log all tasks with their status
    taskResults.forEach(task => {
      console.log(`[MigrationPanel] Task ${task.serial}: upgradeType=${task.upgradeType}, suppressAppsStatus=${task.suppressAppsStatus}, status=${task.status}, configTaskId=${task.configTaskId}, configStatus=${task.configStatus}`);
    });

    const tasksToProcess = taskResults.filter(task =>
      (task.upgradeType === 'VI' || task.upgradeType === 'PSVI') &&
      task.suppressAppsStatus === 'PENDING' &&
      task.status && ['SUCCESS', 'COMPLETED'].includes(task.status.toUpperCase()) &&
      (!task.configTaskId || (task.configStatus && ['SUCCESS', 'COMPLETED'].includes(task.configStatus.toUpperCase())))
    );

    console.log('[MigrationPanel] Tasks to process after filtering:', tasksToProcess.length);
    if (tasksToProcess.length === 0) {
      console.log('[MigrationPanel] No tasks to process - exiting');
      return;
    }

    console.log('[MigrationPanel] Processing sysmon config for devices:', tasksToProcess.map(t => `${t.serial} (${t.upgradeType})`));

    for (const task of tasksToProcess) {
      console.log(`[MigrationPanel] ========== Processing device ${task.serial} (${task.upgradeType}) ==========`);
      console.log(`[MigrationPanel] Environment: ${task.environment || 'prod'}`);
      try {
        // Check if device is registered in IoT Hub (simple check, no message fetching)
        logDebug(`[MigrationPanel] Checking IoT Hub registration for ${task.serial}...`);
        const iotCheckResponse = await axios.post('/api/azure/device/search', {
          serial: task.serial,
        });
        const deviceStatus = iotCheckResponse.data?.status;
        logDebug(`[MigrationPanel] IoT Hub status for ${task.serial}: ${deviceStatus}`);

        if (deviceStatus !== 'found') {
          logDebug(`[MigrationPanel] Device ${task.serial} not registered in IoT Hub (status: ${deviceStatus}), skipping sysmon config`);
          setTaskResults(prev => prev.map(t =>
            t.serial === task.serial ? {
              ...t,
              suppressAppsStatus: 'WAITING_IOT',
              suppressAppsMessage: `Waiting for IoT registration (current: ${deviceStatus || 'unknown'})`
            } : t
          ));
          continue;
        }

        // Device is ready, send sysmon config via Device Twin
        // VI: disable all apps, PSVI: send empty array (no apps disabled)
        const disabledApps = task.upgradeType === 'VI' ? SYSMON_ALL_APPS : [''];
        logDebug(`[MigrationPanel] Sending sysmon config to ${task.serial} (${task.upgradeType}), disabledApps=${JSON.stringify(disabledApps)}`);

        const sysmonConfig = {
          sysmon: {
            disabledApps: disabledApps
          }
        };

        const response = await axios.put('/api/device/config-binding', {
          serial_number: task.serial,
          config: sysmonConfig
        });

        if (response.data.success) {
          const message = task.upgradeType === 'VI'
            ? 'Suppress apps config sent to Device Twin'
            : 'Sysmon config sent to Device Twin (no apps disabled)';
          console.log(`[MigrationPanel] ✅ Successfully sent sysmon config to ${task.serial}`);
          console.log(`[MigrationPanel] Status message: ${message}`);

          setTaskResults(prev => prev.map(t =>
            t.serial === task.serial ? {
              ...t,
              suppressAppsStatus: 'SENT',
              suppressAppsMessage: message,
              lastUpdate: new Date().toISOString()
            } : t
          ));
        } else {
          console.error(`[MigrationPanel] ❌ Failed to send sysmon config to ${task.serial}:`, response.data.error);
          setTaskResults(prev => prev.map(t =>
            t.serial === task.serial ? {
              ...t,
              suppressAppsStatus: 'FAILED',
              suppressAppsMessage: response.data.error || 'Failed to send sysmon config',
              lastUpdate: new Date().toISOString()
            } : t
          ));
        }
      } catch (err) {
        console.error(`[MigrationPanel] ❌ Exception while processing sysmon config for ${task.serial}:`, err);
        console.error(`[MigrationPanel] Error details:`, {
          message: err.message,
          response: err.response?.data,
          status: err.response?.status
        });
        setTaskResults(prev => prev.map(t =>
          t.serial === task.serial ? {
            ...t,
            suppressAppsStatus: 'FAILED',
            suppressAppsMessage: err.response?.data?.error || err.message,
            lastUpdate: new Date().toISOString()
          } : t
        ));
      }
    }
  }, [taskResults, setTaskResults]);

  // Auto-refresh every 1 minute
  useEffect(() => {
    if (autoRefresh && taskResults.length > 0) {
      autoRefreshIntervalRef.current = setInterval(() => {
        refreshTaskStatus();
        processSuppressApps();
      }, 1 * 60 * 1000); // 1 minute

      return () => {
        if (autoRefreshIntervalRef.current) {
          clearInterval(autoRefreshIntervalRef.current);
        }
      };
    }
  }, [autoRefresh, taskResults.length, refreshTaskStatus, processSuppressApps]);

  // Process suppress apps after refresh
  useEffect(() => {
    if (taskResults.length > 0) {
      processSuppressApps();
    }
  }, [taskResults.length, processSuppressApps]);

  // Trigger a one-shot status poll immediately after group-load hydration sets task IDs.
  // The ref guard prevents re-triggering: after refreshTaskStatus fires and updates taskResults,
  // this effect re-runs, but needsInitialStatusPollRef.current is already false so it does nothing.
  // DO NOT remove the ref guard — without it, every status update triggers another poll indefinitely.
  useEffect(() => {
    if (needsInitialStatusPollRef.current && taskResults.length > 0) {
      needsInitialStatusPollRef.current = false;
      refreshTaskStatus();
    }
  }, [taskResults, refreshTaskStatus]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (autoRefreshIntervalRef.current) {
        clearInterval(autoRefreshIntervalRef.current);
      }
    };
  }, []);

  // Get status badge color
  const getStatusBadgeClass = useCallback((status) => {
    const s = (status || '').toUpperCase();
    if (s === 'SUCCESS' || s === 'COMPLETED') {
      return 'bg-green-100 text-green-700 border-green-300';
    }
    if (s === 'FAILURE' || s === 'ERROR') {
      return 'bg-red-100 text-red-700 border-red-300';
    }
    if (s === 'PENDING' || s === 'WAITING') {
      return 'bg-yellow-100 text-yellow-700 border-yellow-300';
    }
    if (s === 'RUNNING' || s === 'IN_PROGRESS' || s === 'UPGRADING') {
      return 'bg-blue-100 text-blue-700 border-blue-300';
    }
    return 'bg-gray-100 text-gray-700 border-gray-300';
  }, []);

  // Group tasks by their group
  const statusGroupOptions = useMemo(() => {
    const optionSet = new Set(selectedGroups.filter(Boolean));
    taskResults.forEach(task => {
      if (task.group) {
        optionSet.add(task.group);
      }
    });
    const sortedGroups = Array.from(optionSet).sort((a, b) => a.localeCompare(b));
    const options = [{ value: 'all', label: 'All groups' }, ...sortedGroups.map(group => ({ value: group, label: group }))];
    if (taskResults.some(task => !task.group)) {
      options.push({ value: 'ungrouped', label: 'No group' });
    }
    return options;
  }, [selectedGroups, taskResults]);

  useEffect(() => {
    if (statusGroupFilter === 'all') return;
    if (!statusGroupOptions.some(option => option.value === statusGroupFilter)) {
      setStatusGroupFilter('all');
    }
  }, [statusGroupFilter, statusGroupOptions, setStatusGroupFilter]);

  const filteredTaskResults = useMemo(() => {
    if (statusGroupFilter === 'all') return taskResults;
    if (statusGroupFilter === 'ungrouped') {
      return taskResults.filter(task => !task.group);
    }
    return taskResults.filter(task => task.group === statusGroupFilter);
  }, [taskResults, statusGroupFilter]);

  const getTaskTimestampValue = (task) => {
    const createdAtTs = task.createdAt ? Date.parse(task.createdAt) : NaN;
    if (!Number.isNaN(createdAtTs)) {
      return createdAtTs;
    }
    const lastUpdateTs = task.lastUpdate ? Date.parse(task.lastUpdate) : NaN;
    if (!Number.isNaN(lastUpdateTs)) {
      return lastUpdateTs;
    }
    return 0;
  };

  const visibleTaskResults = useMemo(() => {
    if (showHistory) {
      return filteredTaskResults;
    }
    const latestByDevice = new Map();
    filteredTaskResults.forEach(task => {
      const key = task.serial || 'unknown';
      const existing = latestByDevice.get(key);
      if (!existing) {
        latestByDevice.set(key, task);
        return;
      }
      const existingTs = getTaskTimestampValue(existing);
      const newTs = getTaskTimestampValue(task);
      if (newTs >= existingTs) {
        latestByDevice.set(key, task);
      }
    });
    return Array.from(latestByDevice.values());
  }, [filteredTaskResults, showHistory]);

  const groupedTaskResults = useMemo(() => {
    const groupMap = {};
    const ungrouped = [];

    visibleTaskResults.forEach(task => {
      if (task.group) {
        if (!groupMap[task.group]) {
          groupMap[task.group] = [];
        }
        groupMap[task.group].push(task);
      } else {
        ungrouped.push(task);
      }
    });

    return { groupMap, ungrouped };
  }, [visibleTaskResults]);

  const visibleGroupOrder = useMemo(() => {
    if (statusGroupFilter === 'all') {
      const ordered = new Set([...selectedGroups, ...Object.keys(groupedTaskResults.groupMap)]);
      return Array.from(ordered);
    }
    if (statusGroupFilter === 'ungrouped') {
      return [];
    }
    return [statusGroupFilter];
  }, [groupedTaskResults.groupMap, selectedGroups, statusGroupFilter]);

  const selectedStatusGroupLabel = useMemo(() => {
    return statusGroupOptions.find(option => option.value === statusGroupFilter)?.label || 'All devices';
  }, [statusGroupOptions, statusGroupFilter]);

  // Render a single upgrade table
  const renderUpgradeTable = useCallback((tableTitle, rows) => (
    <div key={tableTitle} className="border rounded-lg overflow-hidden">
      <div className="bg-gray-50 px-4 py-2 border-b flex items-center justify-between">
        <p className="font-medium text-gray-700">{tableTitle}</p>
        <span className="text-xs text-gray-500">{rows.length} device{rows.length === 1 ? '' : 's'}</span>
      </div>
      <table className="w-full text-sm">
        <thead className="bg-gray-100">
          <tr>
            <th className="text-left p-3 font-medium">Device</th>
            <th className="text-left p-3 font-medium">Type</th>
            <th className="text-left p-3 font-medium">Video Solution</th>
            <th className="text-left p-3 font-medium">Release Version</th>
            <th className="text-left p-3 font-medium">File</th>
            <th className="text-left p-3 font-medium">Upgrade Status</th>
            <th className="text-left p-3 font-medium">Config Status</th>
            <th className="text-left p-3 font-medium">Sysmon Apps</th>
            <th className="text-left p-3 font-medium">Created At</th>
            <th className="text-left p-3 font-medium">Started By</th>
            <th className="text-left p-3 font-medium">Message</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((task, idx) => (
            <tr key={`${task.serial}-${idx}`} className="border-t">
              <td className="p-3 font-mono text-xs">{task.serial}</td>
              <td className="p-3">
                <Badge
                  variant="outline"
                  className={task.deviceType === 'M1N'
                    ? 'bg-blue-50 text-blue-700 border-blue-300'
                    : 'bg-purple-50 text-purple-700 border-purple-300'
                  }
                >
                  {task.deviceType === 'M1N' ? (
                    <Cpu className="w-3 h-3 mr-1" />
                  ) : (
                    <Camera className="w-3 h-3 mr-1" />
                  )}
                  {task.deviceType}
                </Badge>
              </td>
              <td className="p-3">
                <Badge
                  variant="outline"
                  className={task.upgradeType === 'VI'
                    ? 'bg-orange-50 text-orange-700 border-orange-300'
                    : 'bg-teal-50 text-teal-700 border-teal-300'
                  }
                >
                  {task.upgradeType || 'PSVI'}
                </Badge>
              </td>
              <td className="p-3 font-medium text-gray-700">
                {task.releaseVersion || '-'}
              </td>
              <td className="p-3 text-xs text-gray-600 max-w-[150px] truncate" title={task.fileName}>
                {task.fileName || '-'}
              </td>
              <td className="p-3">
                <div className="flex items-center gap-1">
                  <Badge variant="outline" className={getStatusBadgeClass(task.status)}>
                    {task.status || '-'}
                  </Badge>
                  {['FAILURE', 'ERROR'].includes((task.status || '').toUpperCase()) && task.taskId && (
                    <button
                      onClick={() => retryTask(task, 'upgrade')}
                      title="Retry upgrade"
                      className="ml-1 text-xs text-blue-600 hover:text-blue-800 underline whitespace-nowrap"
                    >
                      Retry
                    </button>
                  )}
                </div>
              </td>
              <td className="p-3">
                {task.configTaskId ? (
                  <div className="flex items-center gap-1">
                    <Badge variant="outline" className={getStatusBadgeClass(task.configStatus)}>
                      {task.configStatus || '-'}
                    </Badge>
                    {['FAILURE', 'ERROR'].includes((task.configStatus || '').toUpperCase()) && (
                      <button
                        onClick={() => retryTask(task, 'config')}
                        title="Retry config"
                        className="ml-1 text-xs text-blue-600 hover:text-blue-800 underline whitespace-nowrap"
                      >
                        Retry
                      </button>
                    )}
                  </div>
                ) : (
                  <span className="text-xs text-gray-400">N/A</span>
                )}
              </td>
              <td className="p-3">
                {task.upgradeType === 'VI' ? (
                  task.suppressAppsStatus ? (
                    <div title={task.suppressAppsMessage || ''}>
                      <Badge variant="outline" className={getStatusBadgeClass(task.suppressAppsStatus)}>
                        {task.suppressAppsStatus === 'WAITING_IOT' ? 'Waiting IoT' : task.suppressAppsStatus}
                      </Badge>
                    </div>
                  ) : (
                    <Badge variant="outline" className="bg-gray-100 text-gray-700 border-gray-300">
                      PENDING
                    </Badge>
                  )
                ) : (
                  <span className="text-xs text-gray-400">N/A</span>
                )}
              </td>
              <td className="p-3 text-xs text-gray-600 whitespace-nowrap">
                {formatDateTime(task.createdAt)}
              </td>
              <td className="p-3 text-xs text-gray-700">
                {task.startedBy || '-'}
              </td>
              <td className="p-3 text-xs text-gray-600 max-w-[200px]">
                <div className="space-y-1">
                  <div className="truncate" title={task.message}>
                    <span className="font-medium">Upgrade:</span> {task.message || '-'}
                  </div>
                  {task.configTaskId && (
                    <div className="truncate text-purple-600" title={task.configMessage}>
                      <span className="font-medium">Config:</span> {task.configMessage || '-'}
                    </div>
                  )}
                  {task.upgradeType === 'VI' && task.suppressAppsMessage && (
                    <div className="truncate text-orange-600" title={task.suppressAppsMessage}>
                      <span className="font-medium">Suppress:</span> {task.suppressAppsMessage}
                    </div>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  ), [getStatusBadgeClass, formatDateTime, retryTask]);

  return (
    <div className="space-y-4">
      {/* Firmware Upgrade Card */}
      <Card className="border-2">
        <CardHeader
          className="cursor-pointer hover:bg-gray-50 transition-colors"
          onClick={() => setUpgradeCardExpanded(!upgradeCardExpanded)}
        >
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <HardDrive className="w-6 h-6 text-indigo-600" />
              Device Firmware Upgrade
            </div>
            {upgradeCardExpanded ? (
              <ChevronDown className="w-5 h-5 text-gray-400" />
            ) : (
              <ChevronRight className="w-5 h-5 text-gray-400" />
            )}
          </CardTitle>
          {upgradeCardExpanded && (
            <CardDescription>
              Select a device group and upgrade files for M1N and Dashcam devices
            </CardDescription>
          )}
        </CardHeader>
        {upgradeCardExpanded && (
          <CardContent className="space-y-6">
            {/* Device Type Selection */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Camera className="w-4 h-4" />
                Video Solution
              </Label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {DEVICE_TYPE_OPTIONS.map((option) => {
                  const isSelected = selectedDeviceType === option.id;
                  return (
                    <button
                      key={option.id}
                      type="button"
                      onClick={() => setSelectedDeviceType(option.id)}
                      className={`rounded-xl border-2 p-4 text-left transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-200 ${isSelected
                        ? 'border-indigo-500 bg-indigo-50 shadow-sm'
                        : 'border-gray-200 bg-white hover:border-gray-300'
                        }`}
                      aria-pressed={isSelected}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-semibold text-gray-900">{option.title}</p>
                          <p className="text-xs text-gray-500 mt-1">{option.description}</p>
                        </div>
                        <span
                          className={`h-4 w-4 rounded-full border ${isSelected
                            ? 'border-indigo-500 bg-indigo-500'
                            : 'border-gray-300'
                            }`}
                          aria-hidden="true"
                        />
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Group Selection - Multi-select Dropdown */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Users className="w-4 h-4" />
                Select Device Groups
              </Label>

              <div className="relative" ref={groupDropdownRef}>
                {/* Dropdown trigger */}
                <button
                  type="button"
                  onClick={() => {
                    setGroupDropdownOpen(!groupDropdownOpen);
                    setGroupSearchTerm('');
                  }}
                  disabled={loadingGroups}
                  aria-label="Select device groups"
                  aria-expanded={groupDropdownOpen}
                  className="w-full flex items-center justify-between p-2.5 border rounded-lg bg-white hover:bg-gray-50 transition-colors text-left"
                >
                  {loadingGroups ? (
                    <span className="flex items-center gap-2 text-gray-500">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Loading groups...
                    </span>
                  ) : selectedGroups.length === 0 ? (
                    <span className="text-gray-500">-- Select groups --</span>
                  ) : (
                    <span className="flex items-center gap-2 flex-wrap">
                      <Badge className="bg-indigo-100 text-indigo-700 border-indigo-300">
                        {selectedGroups.length} group(s) selected
                      </Badge>
                      <span className="text-sm text-gray-500">
                        · {groupDevices.length} devices
                      </span>
                    </span>
                  )}
                  <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${groupDropdownOpen ? 'rotate-180' : ''}`} />
                </button>

                {/* Dropdown menu */}
                {groupDropdownOpen && (
                  <div className="absolute z-50 w-full mt-1 bg-white border rounded-lg shadow-lg">
                    {/* Search */}
                    <div className="p-2 border-b">
                      <div className="relative">
                        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                        <Input
                          type="text"
                          autoFocus
                          value={groupSearchTerm}
                          onChange={(e) => setGroupSearchTerm(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Escape') setGroupDropdownOpen(false);
                          }}
                          placeholder="Search groups by name..."
                          className="pl-8 h-9 text-sm"
                        />
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center justify-between p-2 border-b bg-gray-50">
                      <span className="text-xs text-gray-500">
                        {groupSearchTerm.trim()
                          ? `${filteredGroups.length} of ${availableGroups.length} groups`
                          : `${availableGroups.length} groups available`}
                      </span>
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => { e.stopPropagation(); selectAllGroups(); }}
                          disabled={filteredGroups.length === 0}
                          className="h-7 text-xs"
                        >
                          {groupSearchTerm.trim() ? 'Select Shown' : 'Select All'}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => { e.stopPropagation(); deselectAllGroups(); }}
                          disabled={selectedGroups.length === 0}
                          className="h-7 text-xs"
                        >
                          Clear
                        </Button>
                      </div>
                    </div>

                    {/* Group list */}
                    <div className="max-h-60 overflow-y-auto">
                      {filteredGroups.length === 0 ? (
                        <p className="px-3 py-4 text-sm text-gray-500 text-center">
                          No groups match "{groupSearchTerm.trim()}"
                        </p>
                      ) : filteredGroups.map(group => (
                        <label
                          key={group.name}
                          className={`flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-gray-50 transition-colors ${selectedGroups.includes(group.name) ? 'bg-indigo-50' : ''
                            }`}
                        >
                          <input
                            type="checkbox"
                            checked={selectedGroups.includes(group.name)}
                            onChange={() => toggleGroupSelection(group.name)}
                            className="w-4 h-4 text-indigo-600 rounded border-gray-300"
                          />
                          <span className="flex-1 text-sm truncate" title={group.name}>
                            {group.name}
                          </span>
                          {/* /api/device-groups names this device_count; the old `count`
                              read undefined and rendered an empty badge. */}
                          <Badge variant="outline" className="text-xs">
                            {group.device_count ?? group.count ?? 0}
                          </Badge>
                        </label>
                      ))}
                    </div>

                    {/* Done button */}
                    <div className="p-2 border-t bg-gray-50">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setGroupDropdownOpen(false)}
                        className="w-full"
                      >
                        Done
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Device Selection */}
            {selectedGroups.length > 0 && (
              <div className="space-y-2">
                <button
                  type="button"
                  className="w-full flex items-center justify-between gap-2 text-left"
                  onClick={() => setDeviceSelectionCollapsed(prev => !prev)}
                >
                  <Label className="flex items-center gap-2 cursor-pointer">
                    <ListChecks className="w-4 h-4" />
                    Select Devices
                  </Label>
                  <ChevronDown
                    className={`w-4 h-4 text-gray-500 transition-transform ${deviceSelectionCollapsed ? '' : 'rotate-180'}`}
                  />
                </button>
                {!deviceSelectionCollapsed && (
                  <>
                    {groupDevices.length === 0 ? (
                      <div className="p-3 border rounded-lg bg-gray-50 text-sm text-gray-600">
                        Select at least one group to load its devices.
                      </div>
                    ) : (
                      <>
                        <div className="flex flex-wrap items-center justify-between gap-3 text-sm text-gray-600">
                          <span>
                            {selectedGroupDevices.length} of {groupDevices.length} device(s) selected
                          </span>
                          <div className="flex gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={selectAllDevices}
                              disabled={selectedGroupDevices.length === groupDevices.length}
                              className="h-7 text-xs"
                            >
                              Select All
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={clearAllDevices}
                              disabled={selectedGroupDevices.length === 0}
                              className="h-7 text-xs"
                            >
                              Clear
                            </Button>
                          </div>
                        </div>
                        <div className="border rounded-lg max-h-60 overflow-y-auto divide-y">
                          {groupDevices.map(device => {
                            const deviceKey = getDeviceKey(device);
                            const isChecked = selectedDeviceKeySet.has(deviceKey);
                            const isM1n = device.serial?.startsWith('0071');
                            const isDashcam = device.serial?.startsWith('00D2');
                            const deviceTypeLabel = isM1n ? 'M1N' : isDashcam ? 'Dashcam' : 'Other';
                            const deviceTypeBadge = isM1n
                              ? 'bg-blue-50 text-blue-700 border-blue-300'
                              : isDashcam
                                ? 'bg-purple-50 text-purple-700 border-purple-300'
                                : 'bg-gray-50 text-gray-600 border-gray-200';

                            return (
                              <label
                                key={deviceKey}
                                className={`flex items-center gap-3 px-3 py-2 text-sm cursor-pointer transition-colors ${isChecked ? 'bg-indigo-50' : 'hover:bg-gray-50'
                                  }`}
                              >
                                <input
                                  type="checkbox"
                                  checked={isChecked}
                                  onChange={() => toggleDeviceSelection(deviceKey)}
                                  className="w-4 h-4 text-indigo-600 rounded border-gray-300"
                                />
                                <div className="flex-1 min-w-0">
                                  <p className="font-mono text-xs text-gray-900 truncate">{device.serial}</p>
                                  {device.group && (
                                    <p className="text-[11px] text-gray-500 truncate">
                                      Group: {device.group}
                                    </p>
                                  )}
                                </div>
                                <Badge variant="outline" className={`${deviceTypeBadge} text-xs`}>
                                  {deviceTypeLabel}
                                </Badge>
                              </label>
                            );
                          })}
                        </div>
                      </>
                    )}
                  </>
                )}
              </div>
            )}

            {/* Select Release Version */}
            <div className="space-y-3">
              <Label className="flex items-center gap-2">
                <Tag className="w-4 h-4" />
                Select Release Version
              </Label>
              {releasesLoading ? (
                <div className="flex items-center gap-2 p-4 bg-gray-50 rounded">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span className="text-sm text-gray-500">Loading releases...</span>
                </div>
              ) : releaseVersions.length === 0 ? (
                <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 text-yellow-500 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-yellow-700">No releases available</p>
                      <p className="text-xs text-yellow-600">Create a release in Advanced Configs &gt; Release Versions</p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="relative" ref={releaseDropdownRef}>
                  <button
                    type="button"
                    onClick={() => setReleaseDropdownOpen(!releaseDropdownOpen)}
                    className="w-full flex items-center justify-between p-3 border rounded-lg bg-white hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      <Tag className="w-4 h-4 text-gray-500" />
                      {selectedRelease ? (
                        <span className="font-medium">{selectedRelease.name}</span>
                      ) : (
                        <span className="text-gray-500">Select a release...</span>
                      )}
                    </div>
                    <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform ${releaseDropdownOpen ? 'rotate-180' : ''}`} />
                  </button>

                  {releaseDropdownOpen && (
                    <div className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-64 overflow-y-auto">
                      {releaseVersions.map((release) => (
                        <div
                          key={release.name}
                          onClick={() => {
                            setSelectedRelease(release);
                            setReleaseDropdownOpen(false);
                          }}
                          className={`p-3 cursor-pointer hover:bg-gray-50 border-b last:border-b-0 ${selectedRelease?.name === release.name ? 'bg-indigo-50' : ''
                            }`}
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-medium text-gray-900">{release.name}</p>
                              {release.notes && (
                                <p className="text-xs text-gray-500">{release.notes}</p>
                              )}
                            </div>
                            <Badge variant="outline" className="bg-teal-50 text-teal-700 border-teal-300 text-xs">
                              LTA: {release.lta_version}
                            </Badge>
                          </div>
                          <div className="flex gap-4 mt-2 text-xs text-gray-500">
                            <span className="flex items-center gap-1">
                              <Cpu className="w-3 h-3 text-blue-500" />
                              {release.m1n_upgrade_file ? release.m1n_upgrade_file.fileName : 'Not set'}
                            </span>
                            <span className="flex items-center gap-1">
                              <Camera className="w-3 h-3 text-purple-500" />
                              {release.dashcam_upgrade_file ? release.dashcam_upgrade_file.fileName : 'Not set'}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Custom Configuration Selection */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <HardDrive className="w-4 h-4" />
                Custom Configuration (Optional)
              </Label>

              {templatesLoading ? (
                <div className="flex items-center gap-2 p-4 bg-gray-50 rounded">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span className="text-sm text-gray-500">Loading configurations...</span>
                </div>
              ) : configTemplates.length === 0 ? (
                <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 text-yellow-500 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-yellow-700">No configurations available</p>
                      <p className="text-xs text-yellow-600">Create a configuration in Advanced Configs &gt; Custom Configs</p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="relative" ref={configDropdownRef}>
                  <button
                    type="button"
                    onClick={() => setConfigDropdownOpen(!configDropdownOpen)}
                    className="w-full flex items-center justify-between p-3 border rounded-lg bg-white hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      <HardDrive className="w-4 h-4 text-gray-500" />
                      {selectedConfigTemplate ? (
                        <span className="font-medium">
                          {configTemplates.find(t => t.id === selectedConfigTemplate)?.name || 'Select configuration...'}
                        </span>
                      ) : (
                        <span className="text-gray-500">Select configuration...</span>
                      )}
                    </div>
                    <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform ${configDropdownOpen ? 'rotate-180' : ''}`} />
                  </button>

                  {configDropdownOpen && (
                    <div className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-64 overflow-y-auto">
                      {/* Clear selection option */}
                      <div
                        onClick={() => {
                          setSelectedConfigTemplate(null);
                          setConfigDropdownOpen(false);
                        }}
                        className={`p-3 cursor-pointer hover:bg-gray-50 border-b ${!selectedConfigTemplate ? 'bg-gray-100' : ''
                          }`}
                      >
                        <p className="text-sm text-gray-500 italic">None (skip configuration)</p>
                      </div>

                      {/* Config templates list */}
                      {configTemplates.map((template) => (
                        <div
                          key={template.id}
                          onClick={() => {
                            setSelectedConfigTemplate(template.id);
                            setConfigDropdownOpen(false);
                          }}
                          className={`p-3 cursor-pointer hover:bg-gray-50 border-b last:border-b-0 ${selectedConfigTemplate === template.id ? 'bg-indigo-50' : ''
                            }`}
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-medium text-gray-900">{template.name}</p>
                              {template.description && (
                                <p className="text-xs text-gray-500 mt-1">{template.description}</p>
                              )}
                            </div>
                            <Badge variant="outline" className="bg-green-50 text-green-700 border-green-300 text-xs">
                              {new Date(template.created_at).toLocaleDateString()}
                            </Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <p className="text-xs text-gray-500">
                If selected, a custom configuration task will be created after the firmware upgrade
              </p>
            </div>

            {/* Selected Release Summary */}
            {selectedRelease && selectedGroups.length > 0 && (
              <div className="p-4 bg-indigo-50 border border-indigo-200 rounded-lg space-y-3">
                <h4 className="font-medium text-indigo-800 flex items-center gap-2">
                  <Tag className="w-4 h-4" />
                  Upgrade Summary for Release: {selectedRelease.name}
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  {/* M1N Summary */}
                  <div className={`p-3 rounded border ${selectedM1nFile ? 'bg-blue-50 border-blue-200' : 'bg-gray-100 border-gray-200'}`}>
                    <div className="flex items-center gap-2 mb-2">
                      <Cpu className="w-5 h-5 text-blue-500" />
                      <span className="font-medium">M1N Devices ({m1nDevices.length})</span>
                    </div>
                    {selectedM1nFile ? (
                      <p className="text-xs font-mono text-gray-700 truncate">{selectedM1nFile.fileName}</p>
                    ) : (
                      <p className="text-xs text-gray-400">No upgrade file configured for M1N</p>
                    )}
                  </div>

                  {/* Dashcam Summary */}
                  <div className={`p-3 rounded border ${selectedDashcamFile ? 'bg-purple-50 border-purple-200' : 'bg-gray-100 border-gray-200'}`}>
                    <div className="flex items-center gap-2 mb-2">
                      <Camera className="w-5 h-5 text-purple-500" />
                      <span className="font-medium">Dashcam Devices ({dashcamDevices.length})</span>
                    </div>
                    {selectedDashcamFile ? (
                      <p className="text-xs font-mono text-gray-700 truncate">{selectedDashcamFile.fileName}</p>
                    ) : (
                      <p className="text-xs text-gray-400">No upgrade file configured for Dashcam</p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Create Tasks Button */}
            {selectedGroups.length > 0 && selectedRelease && (selectedM1nFile || selectedDashcamFile) && (
              <div className="flex gap-3 pt-4 border-t">
                <Button
                  onClick={handleCreateUpgradeTasks}
                  disabled={isViewer() || taskLoading || selectedGroupDevices.length === 0}
                  className="bg-indigo-600 hover:bg-indigo-700"
                  title={isViewer() ? 'View-only access: Cannot start upgrades' : ''}
                >
                  {taskLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <Upload className="w-4 h-4 mr-2" />
                  )}
                  Start Firmware Upgrade
                </Button>

                {/* Custom Actions Toggle Button - Show when tasks exist */}
                {taskResults.length > 0 && (
                  <Button
                    onClick={() => setShowCustomActions(!showCustomActions)}
                    variant="outline"
                    className={showCustomActions ? 'border-indigo-500 bg-indigo-50' : ''}
                    title="Toggle custom actions (send config, suppress apps)"
                  >
                    <Wrench className="w-4 h-4 mr-2" />
                    Custom Actions
                    <ChevronDown className={`w-4 h-4 ml-2 transition-transform ${showCustomActions ? 'rotate-180' : ''}`} />
                  </Button>
                )}
              </div>
            )}

            {/* Manual Action Buttons - Show when Custom Actions is toggled */}
            {taskResults.length > 0 && showCustomActions && (
              <div className="flex gap-3 pt-3 pb-2 px-4 bg-gray-50 rounded-lg border border-gray-200">
                {/* Send Custom Config Button */}
                {selectedConfigTemplate && (
                  <Button
                    onClick={handleSendCustomConfig}
                    disabled={isViewer() || taskLoading}
                    className="bg-purple-600 hover:bg-purple-700"
                    title={isViewer() ? 'View-only access: Cannot send config' : 'Send custom configuration to completed devices'}
                  >
                    {taskLoading ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : (
                      <Settings className="w-4 h-4 mr-2" />
                    )}
                    Send Custom Config
                  </Button>
                )}

                {/* Send Suppress Apps Button */}
                {(selectedDeviceType === 'VI' || selectedDeviceType === 'PSVI') && (
                  <Button
                    onClick={handleSendSuppressApps}
                    disabled={isViewer() || taskLoading}
                    className="bg-orange-600 hover:bg-orange-700"
                    title={
                      isViewer()
                        ? 'View-only access: Cannot suppress apps'
                        : selectedDeviceType === 'VI'
                          ? 'Suppress all apps (alarms, lta-wifi, geolocation, safety)'
                          : 'Send sysmon config with no apps disabled'
                    }
                  >
                    {taskLoading ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : (
                      <Shield className="w-4 h-4 mr-2" />
                    )}
                    {selectedDeviceType === 'VI' ? 'Suppress All Apps (VI)' : 'Enable All Apps (PSVI)'}
                  </Button>
                )}
              </div>
            )}

            {/* Task Error */}
            {taskError && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
                <XCircle className="w-5 h-5 text-red-500 mt-0.5" />
                <div>
                  <p className="font-medium text-red-700">Error</p>
                  <p className="text-sm text-red-600">{taskError}</p>
                </div>
              </div>
            )}

            {/* Task Results Table */}
            {taskResults.length > 0 && (
              <div className="space-y-3 pt-4 border-t">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold">Upgrade Status</h3>
                  <div className="flex flex-wrap items-center gap-3">
                    <label className="flex items-center gap-2 text-sm text-gray-600">
                      <input
                        type="checkbox"
                        checked={autoRefresh}
                        onChange={(e) => setAutoRefresh(e.target.checked)}
                        className="rounded"
                      />
                      Auto-refresh (1 min)
                    </label>
                    <label className="flex items-center gap-2 text-sm text-gray-600">
                      <input
                        type="checkbox"
                        checked={showHistory}
                        onChange={(e) => setShowHistory(e.target.checked)}
                        className="rounded"
                      />
                      Show full history
                    </label>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-500 uppercase tracking-wide">View</span>
                      <div className="inline-flex rounded-lg border bg-white overflow-hidden">
                        <button
                          type="button"
                          onClick={() => setUpgradeViewMode('combined')}
                          className={`px-3 py-1 text-xs font-medium transition-colors ${upgradeViewMode === 'combined'
                            ? 'bg-indigo-50 text-indigo-600'
                            : 'text-gray-500 hover:bg-gray-50'
                            }`}
                        >
                          All Devices
                        </button>
                        <button
                          type="button"
                          onClick={() => setUpgradeViewMode('grouped')}
                          className={`px-3 py-1 text-xs font-medium transition-colors ${upgradeViewMode === 'grouped'
                            ? 'bg-indigo-50 text-indigo-600'
                            : 'text-gray-500 hover:bg-gray-50'
                            }`}
                        >
                          By Group
                        </button>
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={refreshTaskStatus}
                      disabled={refreshing}
                      className="flex items-center gap-2"
                    >
                      {refreshing ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <RefreshCw className="w-4 h-4" />
                      )}
                      Refresh
                    </Button>
                  </div>
                </div>

                <div className="space-y-1">
                  <Label className="text-xs text-gray-600">Show group</Label>
                  <select
                    value={statusGroupFilter}
                    onChange={(e) => setStatusGroupFilter(e.target.value)}
                    className="flex h-10 w-56 rounded-md border border-input bg-background px-3 py-2 text-sm"
                  >
                    {statusGroupOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                {lastRefresh && (
                  <p className="text-xs text-gray-500 flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    Last updated: {new Date(lastRefresh).toLocaleTimeString()}
                  </p>
                )}

                {upgradeViewMode === 'grouped' ? (
                  <div className="space-y-6">
                    {visibleGroupOrder.map(groupName => {
                      const groupTasks = groupedTaskResults.groupMap[groupName] || [];
                      if (groupTasks.length === 0) return null;
                      return renderUpgradeTable(`${groupName} Devices`, groupTasks);
                    })}
                    {(statusGroupFilter === 'all' || statusGroupFilter === 'ungrouped') && groupedTaskResults.ungrouped.length > 0 && (
                      renderUpgradeTable('Other Devices', groupedTaskResults.ungrouped)
                    )}
                    {visibleGroupOrder.length === 0 && groupedTaskResults.ungrouped.length === 0 && (
                      <p className="text-sm text-gray-500">No groups to display.</p>
                    )}
                    {statusGroupFilter !== 'all' && statusGroupFilter !== 'ungrouped' && (groupedTaskResults.groupMap[statusGroupFilter]?.length || 0) === 0 && (
                      <p className="text-sm text-gray-500">No tasks found for {selectedStatusGroupLabel}.</p>
                    )}
                  </div>
                ) : (
                  renderUpgradeTable(
                    statusGroupFilter === 'all' ? 'All devices' : `${selectedStatusGroupLabel}`,
                    visibleTaskResults
                  )
                )}

                {/* Summary */}
                <div className="flex gap-4 text-sm">
                  <span className="flex items-center gap-1">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    {filteredTaskResults.filter(t => ['SUCCESS', 'COMPLETED'].includes((t.status || '').toUpperCase())).length} completed
                  </span>
                  <span className="flex items-center gap-1">
                    <Loader2 className="w-4 h-4 text-blue-500" />
                    {filteredTaskResults.filter(t => ['RUNNING', 'IN_PROGRESS', 'UPGRADING', 'PENDING', 'WAITING'].includes((t.status || '').toUpperCase())).length} in progress
                  </span>
                  <span className="flex items-center gap-1">
                    <XCircle className="w-4 h-4 text-red-500" />
                    {filteredTaskResults.filter(t => ['FAILED', 'ERROR'].includes((t.status || '').toUpperCase())).length} failed
                  </span>
                </div>
              </div>
            )}
          </CardContent>
        )}
      </Card>
    </div>
  );
}

export default MigrationPanel;
